import { Card } from "@coherence-design-system/controls";
import {
    CheckboxVisibility, IColumn, mergeStyles,
    ScrollablePane, Selection, SelectionMode,
    ShimmeredDetailsList, Spinner, SpinnerSize, TooltipHost
} from "@fluentui/react";
import { Button, Switch } from "@fluentui/react-components";
import { useBoolean, useId } from "@fluentui/react-hooks";
import { calloutProps } from "../../Styles/Page.styles";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import * as React from "react";
import { RenderStickyHeader } from "../../Helpers/StickyHeader";
import HttpService from "../../services/HttpService/HttpService";
import { getHeader } from "../../Styles/Page.styles";
import { useAppInsights } from "../AppInsights/AppInsights";
import { trackException } from "../AppInsights/LoggingHelper";
import { UnauthorizedMessage } from "../Shared/AppConstants";
import { RootContext } from "../Stores/RootStore";
import { Guid } from "guid-typescript";

interface IStaleJobsProps { }

interface IStaleJobs {
    ServiceTreeGUID: string;
    JobName: string;
    JobId: Guid;
} 

const cardSize = mergeStyles({
    width: "70vw",
    height: "40vh",
});

const offboardMessageSuccess = "Successfully Offboarded Job(s)!";
const offboardMessageError = "Could not offboard Job. Please try again.";
export interface IDeleteJob {
    JobIds: Guid[];
    ServiceTreeGUID: String;
}
export const StaleJobs: React.FunctionComponent<IStaleJobsProps> = (props: IStaleJobsProps) => {
    const tooltipId = useId("tooltip");
    const { state } = React.useContext(RootContext); // auth token context
    const [dataGridData, SetDataGridData] = React.useState<IStaleJobs[]>([]);
    const [offboardMessage, SetOffboardMessage] = React.useState<string>("");
    const [JobsLoaded, SetJobsLoaded] = React.useState<boolean>(false);
    const [currentlyOffboarding, SetCurrentlyOffboarding] = React.useState<boolean>(false);
    const [selectionModeJobs, SetSelectionModeJobs] = React.useState<boolean>(false);
    const [isOffboardButtonDisabled, { setTrue: hideOffboardButton, setFalse: showOffboardButton }] = useBoolean(true);
    const [selection] = React.useState(new Selection({
        onSelectionChanged: () => {
            selection.getSelectedIndices().length > 0 ? showOffboardButton() : hideOffboardButton();
        },
    }));


    const appInsights = useAppInsights();

    // HttpService
    const [httpService] = React.useState(HttpService(appInsights, state));

    React.useEffect(() => {
        fetchStaleJobs();
    }, []);

    const fetchStaleJobs = () => {
        SetJobsLoaded(false);

        const getStaleJobs = httpService.get({
            url: "api/patchingjob/getstaleJobs",
            token: state.AuthStore.Token,
            params: {},
        });

        getStaleJobs.then((response: any) => {
            const staleJobs: IStaleJobs[] = [];

            if (response?.data !== UnauthorizedMessage) {
                response?.data.values.forEach((Job: any) => { 
                    staleJobs.push({
                        ServiceTreeGUID: Job.serviceTreeGUID,
                        JobName: Job.jobName,
                        JobId: Job.jobId,
                    });
                });
                
                SetDataGridData(staleJobs);
                SetJobsLoaded(true);
            } else { 
                SetJobsLoaded(true);
                staleJobs.push({
                    ServiceTreeGUID: "",
                    JobName: "",
                    JobId: Guid.createEmpty(),
                });

                SetDataGridData(staleJobs);
            }
        }).catch((error: any) => {
            trackException(appInsights, error, SeverityLevel.Error, "Admin", "Get Arc Jobs Pending Offboarding", "Get Arc Jobs Pending Offboarding", state.AuthStore, {});
        });
    }

    const resultsColumns: IColumn[] = [
        {
            key: "JobName",
            name: "JobName",
            fieldName: "JobName",
            minWidth: 200,
            maxWidth: 250,
            isResizable: true,
        },
        {
            key: "ServiceTreeGUID",
            name: "ServiceTreeGUID",
            fieldName: "ServiceTreeGUID",
            minWidth: 200,
            maxWidth: 250,
            isResizable: true,
        },
    ];

    const onRenderItemColumn = (item?: any, index?: number | undefined, column?: IColumn | undefined): React.ReactNode => {
        if (item !== undefined && column !== undefined) {
            const fieldContent = item[column.fieldName as keyof IStaleJobs] as any;

            switch (column.fieldName) {
                default:
                    return <span>{fieldContent}</span>;
            }
        }
    };

    const   offboardStaleJobs = async () => {
        SetOffboardMessage("");
        SetCurrentlyOffboarding(true);
        const deleteJobsRequests: Array<Promise<any>> = [];
        //const offboardingRequestArr: IDeleteJob[] = [];

        for (let i = 0; i < selection.getSelectedCount(); i++) {
            
            //offboardingRequestArr.push({ 
            //    JobIds: [(selection.getSelection()[i] as IStaleJobs).JobId], 
            //    ServiceTreeGUID: (selection.getSelection()[i] as IStaleJobs).ServiceTreeGUID
            //});  

            const deleteJob: IDeleteJob = {
                JobIds: [(selection.getSelection()[i] as IStaleJobs).JobId],
                ServiceTreeGUID: (selection.getSelection()[i] as IStaleJobs).ServiceTreeGUID
            }

            const deleteRequest = await httpService.delete<any>({
                url: "api/PatchingJob/delete",
                token: state.AuthStore.Token,
                params: deleteJob,
            });

            deleteJobsRequests.push(deleteRequest); 
        }
        Promise.all(deleteJobsRequests).then((resp: any) => {
            SetCurrentlyOffboarding(false);

            if (resp == undefined || resp.data == undefined) {
                SetOffboardMessage(offboardMessageError);
            }

            if (resp.data === UnauthorizedMessage) {
                SetOffboardMessage(offboardMessageSuccess);
            }

            SetOffboardMessage((resp.data.length > 0 && resp.data[0].status == "Failed") ? resp.data[0].message : offboardMessageSuccess);

            fetchStaleJobs();
            SetCurrentlyOffboarding(false);
        }).catch((error: any) => {
            SetOffboardMessage(offboardMessageError);
            trackException(appInsights, error, SeverityLevel.Error, "Admin", "Offboard Stale Arc Jobs", "Offboard Stale Arc Jobs", state.AuthStore, {});
        });
        
    };

    const performOffboarding = (request: IDeleteJob[]) => {
        httpService.post({
            url: "api/patchingjob/delete",
            token: state.AuthStore.Token,
            data: request,
        }).then((resp: any) => {
            SetCurrentlyOffboarding(false);

            if (resp == undefined || resp.data == undefined) {
                SetOffboardMessage(offboardMessageError);
            }

            if (resp.data === UnauthorizedMessage) {
                SetOffboardMessage(offboardMessageSuccess);
            }

            SetOffboardMessage((resp.data.length > 0 && resp.data[0].status == "Failed") ? resp.data[0].message : offboardMessageSuccess);

            fetchStaleJobs();
            SetCurrentlyOffboarding(false);
        }).catch((error: any) => {
            SetOffboardMessage(offboardMessageError);
            trackException(appInsights, error, SeverityLevel.Error, "Admin", "Offboard Stale Arc Jobs", "Offboard Stale Arc Jobs", state.AuthStore, {});
        });
    }

    return (
        <div className={cardSize}>
            <Card header={getHeader("Offboard Stale Jobs", "left")}>
                <Button
                    appearance="primary"
                    disabled={isOffboardButtonDisabled}
                    onClick={offboardStaleJobs}
                >
                    Offboard
                </Button>
                <div hidden={!currentlyOffboarding}>
                    <Spinner label="Offboarding Stale Jobs" size={SpinnerSize.medium} ariaLive="assertive" />
                </div>
                <div>
                    <span>{offboardMessage}</span>
                </div>
                <span>Enable Selection?</span>
                <TooltipHost content="Enable or disable row selection for bulk actions" id={tooltipId} calloutProps={calloutProps}>
                    <Switch
                        aria-label="Enable selection"
                        defaultChecked={selectionModeJobs}
                        onChange={() => SetSelectionModeJobs(!selectionModeJobs)}
                    />
                </TooltipHost>
                <ScrollablePane style={{ height: "25vh", position: "relative" }}>
                    <ShimmeredDetailsList
                        columns={resultsColumns}
                        items={dataGridData}
                        compact={true}
                        onRenderDetailsHeader={RenderStickyHeader}
                        checkboxVisibility={CheckboxVisibility.onHover}
                        onRenderItemColumn={onRenderItemColumn}
                        selectionMode={selectionModeJobs ? SelectionMode.multiple : SelectionMode.none}
                        selection={selection}
                        enableShimmer={!JobsLoaded}
                    />
                </ScrollablePane>
            </Card>
        </div>
    );

};
